// What a buyer can actually DO with each tier of a tiered product, given what
// they already own and what they already subscribe to.
//
// This exists because the product page used to answer that question with one
// boolean ("is the selected tier different from the purchased one?"), which
// offered an Add to Cart / Subscribe button for every tier the buyer did not own
// — including every tier the server would refuse. The rule the checkout actually
// enforces is that a buyer holds ONE entitlement per product (one
// users/{uid}/ownedProducts/{productId} doc carrying one `tierId`), so a second
// tier cannot be bought alongside the first. The only supported way to move
// between tiers is a recurring subscription switching at its billing boundary
// (setSubscriptionTierChange). Everything here is the client mirror of those
// server rules, so the page never renders a CTA that ends in an error.
//
// Pure: no React, no Firestore, no clock. The page passes in what it has.

export type TierActionKind =
    /** Nothing owned here yet — the ordinary buy/subscribe path. */
    | 'buy'
    /** A one-off tier this buyer already bought. */
    | 'current-owned'
    /** The recurring tier this buyer is subscribed to right now. */
    | 'current-sub'
    /** A recurring tier the buyer's subscription can move to at its next boundary. */
    | 'switch'
    /** The recurring tier a switch is already scheduled to. */
    | 'pending'
    /**
     * The buyer's own recurring tier, on a subscription they cancelled, while
     * the cycle they paid for is still running. Taking it again is the one
     * recurring purchase the server allows against a live entitlement.
     */
    | 'resubscribe'
    /** Cannot be had while the buyer's current entitlement stands. */
    | 'unavailable';

export interface TierActionInput {
    /** The tier being asked about. */
    tier: { id: string; recurring: boolean };
    /**
     * Tier of the buyer's LIVE entitlement for this product, or null when they
     * have none (never bought, refunded, or a subscription whose access lapsed).
     * A live entitlement with no recorded tier (a legacy order) reads as '' and
     * matches no tier, which correctly makes every tier unavailable rather than
     * guessing which one they hold.
     */
    ownedTierId: string | null;
    /**
     * The buyer's subscription to this product, when there is a switchable one.
     * Null for one-off owners, for whole-product subscriptions (no tiers to move
     * between), and for canceled/incomplete subscriptions, which have no future
     * boundary to apply a change at.
     */
    subscription: { tierId: string; pendingTierId?: string } | null;
    /**
     * True when the live entitlement comes from a subscription the buyer
     * cancelled and whose paid-for cycle has not run out yet.
     *
     * It changes two answers. Their own tier becomes re-takeable, because
     * createAirwallexPaymentIntent allows a fresh subscription to the SAME tier
     * against a live entitlement (that guard exists to stop a SECOND tier being
     * stacked, not to stop someone changing their mind). And the other tiers are
     * blocked only until that access ends, rather than permanently, so they must
     * not be explained as tiers the buyer owns outright.
     */
    entitlementLapsing?: boolean;
}

export interface TierAction {
    kind: TierActionKind;
    /** Why this tier is unavailable, for the buyer. Empty for every other kind. */
    reason: string;
}

export function resolveTierAction({
    tier,
    ownedTierId,
    subscription,
    entitlementLapsing = false,
}: TierActionInput): TierAction {
    // No live entitlement: the buyer is free to take any tier. A canceled
    // subscription whose access has lapsed lands here too, which is what lets
    // them resubscribe at whichever tier they now want.
    if (ownedTierId === null) return { kind: 'buy', reason: '' };

    const isCurrent = ownedTierId === tier.id;

    if (subscription) {
        if (isCurrent) return { kind: 'current-sub', reason: '' };
        // A subscription can only ever become another RECURRING tier. A one-off
        // tier is a purchase, not a cadence, so moving to it would mean
        // cancelling and buying — a decision the buyer makes deliberately, not a
        // side effect of picking a tier.
        if (!tier.recurring) {
            return {
                kind: 'unavailable',
                reason: 'This is a one-time purchase, so your subscription cannot be switched to it. ' +
                    'Cancel your subscription first if you want to buy it outright.',
            };
        }
        if (subscription.pendingTierId === tier.id) return { kind: 'pending', reason: '' };
        return { kind: 'switch', reason: '' };
    }

    // Held, but with no subscription to switch. Either owned outright, or held
    // on a cancelled subscription that is running out its paid-for cycle.
    if (isCurrent) {
        return entitlementLapsing && tier.recurring
            ? { kind: 'resubscribe', reason: '' }
            : { kind: 'current-owned', reason: '' };
    }
    if (entitlementLapsing) {
        return {
            kind: 'unavailable',
            reason: 'Your access to this product is on another tier until your cancelled ' +
                'subscription runs out. You can take this tier once it does.',
        };
    }
    // Owned outright. There is no second entitlement to grant and no billing
    // boundary to change anything at, so nothing else on this product is
    // purchasable.
    return {
        kind: 'unavailable',
        reason: 'You already own another tier of this product, and you can only hold one. ' +
            'To move up, contact the seller.',
    };
}

/**
 * True when this action describes the tier the buyer ALREADY HOLDS, as opposed
 * to one they could still take.
 *
 * The product page splits the tier list on this: a held tier is listed on its
 * own rather than in the row of purchasable ones, because a price sitting in a
 * comparison list reads as an offer, and the buyer has already paid it. Note
 * that 'pending' is NOT held — it is the tier a scheduled switch is heading
 * TO, which the buyer starts paying for at the next boundary, so it belongs
 * with the tiers still ahead of them.
 */
export function isOwnedTierAction(kind: TierActionKind): boolean {
    return kind === 'current-owned' || kind === 'current-sub' || kind === 'resubscribe';
}

/** The short badge shown on the tier row itself. Empty when it needs none. */
export function tierActionBadge(kind: TierActionKind): string {
    switch (kind) {
    case 'current-owned': return 'Purchased';
    case 'current-sub': return 'Your tier';
    case 'resubscribe': return 'Your tier';
    case 'pending': return 'Switching to this';
    default: return '';
    }
}
